/**
 * Unified feed row — replaces the separate mock notification and activity rows.
 * Renders one root event as a glass pill; clicking a row with downstream
 * effects expands an inline cause → effect tree.
 *
 * Long-press (or right-click) reveals a personal "Mark as reviewed" action so
 * users can declutter their timeline without affecting teammates.
 */
import { type CSSProperties, useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Sparkles,
  X,
} from "lucide-react";

import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { formatDimensionValue } from "@/lib/formatDimensionValue";

import { formatFeedPartyLabel } from "./feedViewerParty";
import { extractLaneLocodes, formatLaneKey } from "@/lib/formatPort";
import { isGraphMailDocumentFilename } from "@/lib/graphMailDocuments";
import { cn } from "@/lib/utils";
import { SensitiveText } from "@/components/common/Sensitive";
import { useFeedOptional } from "@/context/FeedContext";
import { useLongPress } from "@/hooks/useLongPress";
import { useAskKai } from "@/hooks/useAskKai";
import { buildAskKaiFeedEventPrompt } from "@/features/kai/kaiFeedEventContext";
import { FormattedFeedText } from "./FormattedFeedText";
import type {
  AffectedFeed,
  FeedEvent,
  GraphMailAttachmentRef,
  GraphMailPayload,
} from "@/types/feed";
import { FeedEventDetail } from "./FeedEventDetail";
import { FeedEventCardMeta, FeedReviewedMark } from "./FeedEventCardMeta";
import {
  feedEventCardAccentClass,
  feedEventPrimaryTypeLabel,
  formatRelativeTime,
  FEED_SEVERITY_BORDER,
  SCOPE_TAG_LABEL,
} from "./feedVisuals";
import { feedRowCornerLinks } from "./feedLinks";
import { FeedRowCornerActions } from "./FeedRowCornerActions";
import { FeedInsightDetail, hasInsightDetail } from "./FeedInsightDetail";
import { BatchIngestedView, isBatchOrgPayload } from "./BatchIngestedView";
import {
  RateCardsIngestedFeedDetail,
  isRateIngestPayload,
} from "./RateCardsIngestedFeedDetail";
import {
  FeedOperationalDigestDetail,
  isOperationalDigestPayload,
} from "./FeedOperationalDigestDetail";
import {
  FeedCustomEventDetail,
  isCustomEventPayload,
} from "./FeedCustomEventDetail";
import {
  FeedCommercialDigestDetail,
  isCommercialDigestPayload,
} from "./FeedCommercialDigestDetail";
import {
  FeedBookingOpportunityDetail,
  isBookingOpportunityPayload,
} from "./FeedBookingOpportunityDetail";
import {
  FeedRebookConfirmDetail,
  isRebookConfirmPayload,
} from "./FeedRebookConfirmDetail";
import {
  FeedRebookProgressDetail,
  isRebookProgressPayload,
} from "./FeedRebookProgressDetail";
import { FeedAttachmentDownloads } from "./FeedAttachmentDownloads";
import { GraphMailFeedDetail, isGraphMailPayload } from "./GraphMailFeedDetail";
import {
  NewBookingMailFeedDetail,
  feedEventDisplaySummary,
  feedEventDisplayTitle,
  isNewBookingMailPayload,
} from "./NewBookingMailFeedDetail";
import { useRateIngestMeta } from "./useRateIngestMeta";
import { GraphMailAttachmentDownloads } from "./GraphMailAttachmentDownloads";
import { feedPrivacyScope } from "./feedPrivacy";
import { feedEventDisplayIso } from "./feedEventTime";
import { FeedRowSlot } from "./FeedRowSlot";
import { useFeedRowReview } from "./useFeedRowReview";

interface FeedEventRowProps {
  event: FeedEvent;
  index: number;
  entering?: boolean;
  exiting?: boolean;
  /** Override the default index × 70ms stagger (home feed tile sync). */
  enterDelayMs?: number;
  /** Reverse stagger when the notification panel collapses. */
  exitDelayMs?: number;
  /** Whether the event is newer than the user's last-seen marker. */
  unread?: boolean;
  /** Fallback review handler when rendered outside `FeedProvider` (e.g. booking panel). */
  onDismiss?: (eventId: string) => void | Promise<void>;
  /** 10s live-arrival border glow after the viewer is positioned on this row. */
  highlighted?: boolean;
}

export function FeedEventRow({
  event,
  index,
  entering = false,
  exiting = false,
  enterDelayMs,
  exitDelayMs,
  unread = false,
  onDismiss,
  highlighted = false,
}: FeedEventRowProps) {
  const feed = useFeedOptional();
  const { preferences } = useUserPreferences();
  const lanePortNames = usePortNames(
    extractLaneLocodes(event.entity_scope?.lane_key),
  );
  const rowRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [detailMounted, setDetailMounted] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const { removing, startReview } = useFeedRowReview();
  const hasEffects = event.children_count > 0;
  const insightDetail = hasInsightDetail(event.payload);
  const graphMail = isGraphMailPayload(event.payload) ? event.payload : null;
  const newBookingMail = isNewBookingMailPayload(event.payload)
    ? event.payload
    : null;
  const batchOrg = isBatchOrgPayload(event.payload) ? event.payload : null;
  const rateIngest = isRateIngestPayload(event.payload) ? event.payload : null;
  const rateIngestMeta = useRateIngestMeta(event.id, event.payload);
  const displaySummary = feedEventDisplaySummary(event, rateIngestMeta);
  const operationalDigest = isOperationalDigestPayload(event.payload)
    ? event.payload
    : null;
  const customEvent = isCustomEventPayload(event.payload)
    ? event.payload
    : null;
  const commercialDigest = isCommercialDigestPayload(event.payload)
    ? event.payload
    : null;
  const bookingOpportunity = isBookingOpportunityPayload(event.payload)
    ? event.payload
    : null;
  const rebookConfirm = isRebookConfirmPayload(event.payload)
    ? event.payload
    : null;
  const rebookProgress = isRebookProgressPayload(event.payload)
    ? event.payload
    : null;
  const batchAttachments = batchAttachmentRefs(event);
  const graphMailAttachments = graphMailAttachmentRefs(graphMail);
  const cornerLinks = feedRowCornerLinks(event, {
    operational: operationalDigest,
    commercial: commercialDigest,
  });
  const scopeChips = buildScopeChips(
    event,
    preferences.showLocodes,
    lanePortNames,
  );
  const hasExpandableDetail =
    hasEffects ||
    insightDetail ||
    !!graphMail ||
    !!newBookingMail ||
    !!batchOrg ||
    !!rateIngest ||
    !!operationalDigest ||
    !!customEvent ||
    !!commercialDigest ||
    !!bookingOpportunity ||
    !!rebookConfirm ||
    !!rebookProgress;
  const expandable = hasExpandableDetail || scopeChips.length > 0;
  const privacyScope = feedPrivacyScope(event);
  const isReviewed = event.is_reviewed === true;
  const typeLabel = feedEventPrimaryTypeLabel(
    event.event_type,
    graphMail?.category ?? null,
    event.payload,
  );
  const accentClass = feedEventCardAccentClass(event.payload, event.event_type);
  const statusLabel =
    event.impact_tier === "high" ? "High impact" : null;
  // A reviewed event can't be reviewed again; the action is only offered on
  // still-active rows (All / Reviewed views surface a badge instead).
  const canReview = (!!feed || !!onDismiss) && !isReviewed;
  const askKai = useAskKai();

  const openActions = () => {
    if (removing) return;
    setActionsOpen(true);
  };

  const handleAskKai = () => {
    setActionsOpen(false);
    askKai(buildAskKaiFeedEventPrompt(event), {
      feedEvent: event,
    });
  };

  const longPress = useLongPress(openActions);

  useEffect(() => {
    if (!expanded) {
      setDetailOpen(false);
      return;
    }
    setDetailMounted(true);
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setDetailOpen(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [expanded]);

  useEffect(() => {
    if (!actionsOpen) return;
    const close = (pointerEvent: PointerEvent) => {
      if (rowRef.current?.contains(pointerEvent.target as Node)) return;
      setActionsOpen(false);
    };
    const id = window.setTimeout(() => {
      document.addEventListener("pointerdown", close);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", close);
    };
  }, [actionsOpen]);

  const rowStyle: CSSProperties | undefined =
    entering || exiting || removing
      ? {
          animationDelay: removing
            ? "0ms"
            : entering
              ? `${enterDelayMs ?? index * 70}ms`
              : `${exitDelayMs ?? index * 50}ms`,
        }
      : undefined;

  const handleReview = () => {
    if (!canReview || removing) return;
    setActionsOpen(false);
    setExpanded(false);
    // Prefer the caller's dismiss handler (feed page / booking panel) so the
    // visible list updates; fall back to the shared live stream.
    startReview(async () => {
      if (onDismiss) await onDismiss(event.id);
      else if (feed) await feed.reviewEvents([event.id]);
    });
  };

  return (
    <FeedRowSlot exiting={removing}>
    <article
      ref={rowRef}
      data-feed-event-id={event.id}
      className={cn(
        "rail-notification-pill glass-pill clay feed-event-row flex w-full min-w-0 max-w-full flex-col overflow-hidden px-3.5 py-2.5",
        entering && !removing && "rail-notification-pill--enter",
        exiting && !removing && "rail-notification-pill--exit",
        removing && "rail-notification-pill--review-exit",
        expanded && "rail-notification-pill--expanded min-h-0",
        actionsOpen && "feed-event-row--actions-open",
        highlighted && "feed-event-row--live-focus",
        accentClass ?? FEED_SEVERITY_BORDER[event.severity],
      )}
      style={rowStyle}
      onContextMenu={(e) => {
        e.preventDefault();
        openActions();
      }}
      onPointerDown={longPress.onPointerDown}
      onPointerMove={longPress.onPointerMove}
      onPointerUp={longPress.onPointerUp}
      onPointerLeave={longPress.onPointerLeave}
      onPointerCancel={longPress.onPointerCancel}
      onClickCapture={longPress.onClickCapture}
    >
      <div className="box-border grid min-h-11 w-full min-w-0 max-w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1.5 sm:gap-x-3">
        <button
          type="button"
          onClick={() => expandable && setExpanded((v) => !v)}
          aria-expanded={expandable ? expanded : undefined}
          className={cn(
            "col-start-1 row-start-1 min-w-0 overflow-hidden text-left",
            expandable ? "cursor-pointer" : "cursor-default",
            (displaySummary ||
              hasEffects ||
              (expanded && scopeChips.length > 0)) &&
              "row-span-2",
          )}
        >
          <FeedEventCardMeta
            typeLabel={typeLabel}
            severity={event.severity}
            horizon={event.time_horizon}
            statusLabel={statusLabel}
            className="mb-1"
          />
          <div className="flex min-w-0 items-start gap-2">
            {unread && (
              <span
                className="mt-1.5 size-1.5 shrink-0 rounded-full bg-sky-400 shadow-[0_0_6px_1px_rgba(90,205,255,0.55)]"
                title="Unread"
                aria-label="Unread"
              />
            )}
            <p
              className={cn(
                "min-w-0 flex-1 text-sm font-medium leading-tight",
                expanded ? "whitespace-normal break-words" : "truncate",
              )}
            >
              <FormattedFeedText
                text={feedEventDisplayTitle(event)}
                scope={privacyScope}
              />
            </p>
          </div>
          {(displaySummary ||
            hasEffects ||
            (expanded && scopeChips.length > 0)) && (
            <div className="mt-1.5 min-w-0 overflow-hidden">
              {displaySummary && (
                <p
                  className={cn(
                    "text-xs text-muted-foreground",
                    expanded
                      ? "whitespace-normal break-words leading-snug"
                      : "truncate",
                  )}
                >
                  <FormattedFeedText
                    text={displaySummary}
                    scope={privacyScope}
                  />
                </p>
              )}
              {hasEffects && (
                <span className="mt-1 text-[10px] font-medium text-primary/80">
                  {event.children_count} downstream effect
                  {event.children_count === 1 ? "" : "s"}
                </span>
              )}
              {expanded && scopeChips.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {scopeChips.map((chip) => (
                    <span
                      key={chip.key}
                      className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground"
                    >
                      {chip.label} ·{" "}
                      {chip.mask ? (
                        <SensitiveText value={chip.value} />
                      ) : (
                        chip.value
                      )}
                      {typeof chip.count === "number" && ` (${chip.count})`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </button>

        <div className="col-start-2 row-start-1 flex shrink-0 flex-col items-end gap-1 self-start pt-0.5">
          <div className="flex items-start gap-1">
            <span className="flex flex-col items-end">
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {formatRelativeTime(feedEventDisplayIso(event))}
              </span>
              {isReviewed && <FeedReviewedMark className="mt-0.5" />}
            </span>
            {expandable && (
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={expanded ? "Collapse" : "Expand"}
                onClick={() => setExpanded((v) => !v)}
                className="rounded-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronDown
                  className={cn(
                    "size-3.5 transition-transform duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
                    expanded && "rotate-180",
                  )}
                />
              </button>
            )}
          </div>
          <FeedRowCornerActions
            links={cornerLinks}
            onMarkDone={
              bookingOpportunity && canReview ? handleReview : undefined
            }
          />
        </div>
      </div>

      {batchAttachments.length > 0 && (
        <FeedAttachmentDownloads attachments={batchAttachments} />
      )}

      {graphMailAttachments.length > 0 && (
        <GraphMailAttachmentDownloads attachments={graphMailAttachments} />
      )}

      {hasExpandableDetail && detailMounted && (
        <div
          className={cn(
            "feed-row-detail min-h-0 min-w-0",
            detailOpen && "feed-row-detail--open",
          )}
          onTransitionEnd={(e) => {
            if (e.propertyName !== "grid-template-rows" || detailOpen) return;
            setDetailMounted(false);
          }}
        >
          <div className="feed-row-detail__inner">
            {/* Daily operational digest: the day's bookings for one dimension,
                with status flags, CTA chips, and a show-more into bookings. */}
            {operationalDigest && (
              <FeedOperationalDigestDetail payload={operationalDigest} />
            )}
            {/* Backoffice custom-event rule card: grouped (or per-booking)
                operational reminder with templated copy, property rows, inline
                bookings, and a show-more into the matching bookings. */}
            {!operationalDigest && customEvent && (
              <FeedCustomEventDetail payload={customEvent} />
            )}
            {/* Per-booking opportunity: a single actionable rebook / first-book
                row with its full pricing comparison. Replaces the grouped
                commercial digest — one reviewable booking per card. */}
            {!operationalDigest && !customEvent && bookingOpportunity && (
              <FeedBookingOpportunityDetail payload={bookingOpportunity} />
            )}
            {/* Legacy per-search-cycle commercial digest (retired; kept for any
                un-migrated cards): the cycle's rebook / first-book opportunities
                for one portfolio owner, with a show-more into the bookings. */}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              commercialDigest && (
                <FeedCommercialDigestDetail payload={commercialDigest} />
              )}
            {/* Per-collector-run rebook confirmations: external rebooks
                (contract switch / drop) detected from agent data this run,
                grouped by the confirming agent feed. */}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              rebookConfirm && (
                <FeedRebookConfirmDetail payload={rebookConfirm} />
              )}
            {/* Internal rebook-workflow milestone: a single booking's
                accept / agent-ack / cancellation step with its pricing
                comparison. Shares the Rebook slice with opportunities. */}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              rebookProgress && (
                <FeedRebookProgressDetail payload={rebookProgress} />
              )}
            {/* Enriched rate-ingest cards render every carrier's stats and
                price moves from the root payload (its per-carrier children are
                only for list-level grouping). */}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              rateIngest && (
                <RateCardsIngestedFeedDetail
                  eventId={event.id}
                  payload={rateIngest}
                />
              )}
            {/* Org ingest roots show the per-portfolio breakdown *and* (when
                present) the aggregated state-change effects below it. */}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              !rateIngest &&
              batchOrg && <BatchIngestedView payload={batchOrg} />}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              !rateIngest &&
              hasEffects && <FeedEventDetail eventId={event.id} />}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              !rateIngest &&
              !hasEffects &&
              !batchOrg &&
              insightDetail && (
                <FeedInsightDetail payload={event.payload} eventId={event.id} />
              )}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              !rateIngest &&
              !hasEffects &&
              !batchOrg &&
              !insightDetail &&
              graphMail && (
                <div className="feed-row-detail-body">
                  <GraphMailFeedDetail payload={graphMail} />
                </div>
              )}
            {!operationalDigest &&
              !customEvent &&
              !bookingOpportunity &&
              !commercialDigest &&
              !rebookConfirm &&
              !rebookProgress &&
              !rateIngest &&
              !hasEffects &&
              !batchOrg &&
              !insightDetail &&
              !graphMail &&
              newBookingMail && (
                <div className="feed-row-detail-body">
                  <NewBookingMailFeedDetail payload={newBookingMail} />
                </div>
              )}
          </div>
        </div>
      )}

      {actionsOpen && (
        <div
          className="feed-row-actions"
          role="toolbar"
          aria-label="Feed row actions"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="feed-row-actions__kai"
            onClick={handleAskKai}
          >
            <Sparkles className="size-3.5" aria-hidden />
            Ask Kai
          </button>
          {canReview && (
            <button
              type="button"
              className="feed-row-actions__remove"
              onClick={handleReview}
            >
              <Check className="size-3.5" aria-hidden />
              Mark as reviewed
            </button>
          )}
          <button
            type="button"
            className="feed-row-actions__cancel"
            aria-label="Cancel"
            onClick={() => setActionsOpen(false)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
    </article>
    </FeedRowSlot>
  );
}

/**
 * Source-file attachments carried by a `batch_ingested` root (org-wide or
 * scoped), used to render download chips. Returns an empty array for any other
 * event type or a batch payload with no attachments.
 */
export function batchAttachmentRefs(event: FeedEvent) {
  if (
    event.event_type !== "batch_ingested" &&
    event.event_type !== "rate_cards_ingested"
  )
    return [];
  const payload = event.payload as { attachments?: unknown };
  const attachments = payload?.attachments;
  if (!Array.isArray(attachments)) return [];
  return attachments.filter(
    (a): a is { batch_id: string; attachment_id: string; filename: string } =>
      !!a &&
      typeof a === "object" &&
      typeof (a as Record<string, unknown>).batch_id === "string" &&
      typeof (a as Record<string, unknown>).attachment_id === "string" &&
      typeof (a as Record<string, unknown>).filename === "string",
  );
}

export function graphMailAttachmentRefs(
  graphMail: GraphMailPayload | null,
): GraphMailAttachmentRef[] {
  if (!graphMail?.attachments?.length) return [];
  return graphMail.attachments.filter(
    (a): a is GraphMailAttachmentRef =>
      !!a &&
      typeof a.mailbox_id === "string" &&
      typeof a.message_id === "string" &&
      typeof a.attachment_id === "string" &&
      typeof a.filename === "string" &&
      isGraphMailDocumentFilename(a.filename),
  );
}

export interface FeedScopeChip {
  key: string;
  label: string;
  value: string;
  mask: boolean;
  /** Optional booking count rendered (unmasked) after the value. */
  count?: number;
}

/** The `affected_feeds` breakdown on a grouped carrier org card, if present. */
function affectedFeeds(payload: FeedEvent["payload"]): AffectedFeed[] {
  if (!payload || typeof payload !== "object") return [];
  const raw = (payload as Record<string, unknown>).affected_feeds;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (f): f is AffectedFeed =>
      !!f &&
      typeof f === "object" &&
      typeof (f as AffectedFeed).dimension === "string" &&
      typeof (f as AffectedFeed).key === "string" &&
      typeof (f as AffectedFeed).booking_count === "number",
  );
}

/**
 * Human-readable scope chips (e.g. "Customer · Acme", "Lane · CNNGB→ILASH")
 * shown only after a row expands, so collapsed cards stay title + description.
 *
 * For a grouped carrier card (one non-duplicated admin row), the `affected_feeds`
 * breakdown drives one chip per portfolio owner it touched — with the booking
 * count — instead of a single homogeneous owner tag.
 */
export function buildScopeChips(
  event: Pick<FeedEvent, "entity_scope" | "payload">,
  showLocodes: boolean,
  portNames: ReadonlyMap<string, string>,
): FeedScopeChip[] {
  const scope = event.entity_scope ?? {};
  const affected = affectedFeeds(event.payload);
  const chips: FeedScopeChip[] = [];
  if (scope.consignee) {
    chips.push({
      key: `consignee:${scope.consignee}`,
      label: SCOPE_TAG_LABEL.customer,
      value: formatDimensionValue("consignee", scope.consignee),
      mask: true,
    });
  }
  if (affected.length > 0) {
    for (const feed of affected) {
      chips.push({
        key: `${feed.dimension}:${feed.key}`,
        label: SCOPE_TAG_LABEL[feed.dimension] ?? feed.dimension,
        value: formatFeedPartyLabel(feed.dimension, feed.key),
        mask: true,
        count: feed.booking_count,
      });
    }
  } else {
    if (scope.sales_person) {
      chips.push({
        key: `sales:${scope.sales_person}`,
        label: SCOPE_TAG_LABEL.sales_person,
        value: formatFeedPartyLabel("sales_person", scope.sales_person),
        mask: true,
      });
    }
    if (scope.opened_by) {
      chips.push({
        key: `opened_by:${scope.opened_by}`,
        label: SCOPE_TAG_LABEL.opened_by,
        value: formatFeedPartyLabel("opened_by", scope.opened_by),
        mask: true,
      });
    }
    if (scope.agent) {
      chips.push({
        key: `agent:${scope.agent}`,
        label: SCOPE_TAG_LABEL.agent,
        value: formatFeedPartyLabel("agent", scope.agent),
        mask: true,
      });
    }
  }
  if (scope.carrier) {
    chips.push({
      key: `carrier:${scope.carrier}`,
      label: SCOPE_TAG_LABEL.carrier,
      value: scope.carrier.toUpperCase(),
      mask: false,
    });
  }
  if (scope.lane_key) {
    chips.push({
      key: `lane:${scope.lane_key}`,
      label: SCOPE_TAG_LABEL.lane,
      value: formatLaneKey(scope.lane_key, showLocodes, portNames),
      mask: false,
    });
  }
  return chips;
}
